import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = 'default' }: { label: string; value: ReactNode; hint?: string; tone?: 'default' | 'good' | 'warn' | 'bad' }) {
  const toneClass =
    tone === 'good' ? 'text-seagreen-600' : tone === 'warn' ? 'text-amber-600' : tone === 'bad' ? 'text-rose-600' : 'text-slate-900';
  return (
    <div className="card px-4 py-3.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-bold tabular-nums ${toneClass}`}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

// One entry per status/state string returned by M17 Planning, M19 Housing,
// or M20 Infrastructure (see src/lib/types.ts for the source enums each
// value comes from). Many values recur verbatim across modules with the
// same real-world meaning (PENDING, APPROVED, REJECTED, CLOSED, ...) so they
// share one tone; module-specific comments mark first use only.
const STATUS_TONES: Record<string, string> = {
  // ── Shared baseline ──────────────────────────────────────────────────
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PAID: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DRAFT: 'bg-slate-100 text-slate-600 ring-slate-200',
  PENDING: 'bg-sky-50 text-sky-700 ring-sky-200',
  SUBMITTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  UNDER_REVIEW: 'bg-sky-50 text-sky-700 ring-sky-200',
  REJECTED: 'bg-rose-50 text-rose-700 ring-rose-200',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-200',
  REVOKED: 'bg-rose-50 text-rose-700 ring-rose-200',
  CANCELLED: 'bg-slate-100 text-slate-500 ring-slate-200',
  CLOSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  SUSPENDED: 'bg-amber-50 text-amber-700 ring-amber-200',

  // ── M17 Planning — PlanningApplication.state ─────────────────────────
  VALIDATION: 'bg-sky-50 text-sky-700 ring-sky-200',
  REFERRALS_NOTICE: 'bg-sky-50 text-sky-700 ring-sky-200',
  ASSESSMENT: 'bg-sky-50 text-sky-700 ring-sky-200',
  COMMITTEE: 'bg-sky-50 text-sky-700 ring-sky-200',
  REFUSED: 'bg-rose-50 text-rose-700 ring-rose-200',
  PERMIT_ISSUED: 'bg-violet-50 text-violet-700 ring-violet-200',
  INSPECTIONS: 'bg-sky-50 text-sky-700 ring-sky-200',
  OCCUPANCY_CERTIFIED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  WITHDRAWN: 'bg-slate-100 text-slate-500 ring-slate-200',
  // Referral.status / ReferralDecision
  RESPONDED: 'bg-sky-50 text-sky-700 ring-sky-200',
  DEEMED_NO_OBJECTION: 'bg-amber-50 text-amber-700 ring-amber-200',
  NO_OBJECTION: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  CONDITIONAL: 'bg-amber-50 text-amber-700 ring-amber-200',
  OBJECTION: 'bg-rose-50 text-rose-700 ring-rose-200',
  // PublicObjection.status
  RECEIVED: 'bg-sky-50 text-sky-700 ring-sky-200',
  BUNDLED: 'bg-violet-50 text-violet-700 ring-violet-200',
  CONSIDERED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // BuildingPermit.status
  ISSUED: 'bg-violet-50 text-violet-700 ring-violet-200',
  COMMENCED: 'bg-sky-50 text-sky-700 ring-sky-200',
  COMPLETE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // SiteInspection.status / .result
  BOOKED: 'bg-violet-50 text-violet-700 ring-violet-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PASS: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  FAIL: 'bg-rose-50 text-rose-700 ring-rose-200',
  HOLD: 'bg-amber-50 text-amber-700 ring-amber-200',
  // EnforcementCase.status — OPEN here means "case just opened, not yet
  // actioned" (this app has no "office hours" concept), so it reads as an
  // attention/pending state rather than a settled-good one.
  OPEN: 'bg-sky-50 text-sky-700 ring-sky-200',
  NOTICE_ISSUED: 'bg-amber-50 text-amber-700 ring-amber-200',
  COMPLIANT: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  NON_COMPLIANT: 'bg-rose-50 text-rose-700 ring-rose-200',
  PROSECUTION_REFERRED: 'bg-rose-50 text-rose-700 ring-rose-200',

  // ── M19 Housing — HousingApplication.state ───────────────────────────
  APPLIED: 'bg-sky-50 text-sky-700 ring-sky-200',
  AUTO_VERIFY: 'bg-sky-50 text-sky-700 ring-sky-200',
  SCORED: 'bg-sky-50 text-sky-700 ring-sky-200',
  QUEUED: 'bg-sky-50 text-sky-700 ring-sky-200',
  ROUND_ELIGIBLE: 'bg-sky-50 text-sky-700 ring-sky-200',
  ALLOCATED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  TENANCY: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PURCHASE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  WAITLISTED: 'bg-amber-50 text-amber-700 ring-amber-200',
  // IncomeDeclaration.status
  ATTESTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // HousingUnit.status
  PLANNED: 'bg-slate-100 text-slate-600 ring-slate-200',
  BUILT: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  OCCUPIED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // AllocationRound.status
  PUBLISHED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // AllocationDecisionType
  ALLOCATE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  WAITLIST: 'bg-amber-50 text-amber-700 ring-amber-200',
  REJECT: 'bg-rose-50 text-rose-700 ring-rose-200',
  // LoanSubsidyApplication.state
  DISBURSED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // Tenancy.state
  ARREARS: 'bg-rose-50 text-rose-700 ring-rose-200',
  TERMINATED: 'bg-slate-100 text-slate-500 ring-slate-200',
  // RentInvoice.status
  OVERDUE: 'bg-rose-50 text-rose-700 ring-rose-200',
  // MaintenanceRequest.priority
  LOW: 'bg-slate-100 text-slate-600 ring-slate-200',
  MEDIUM: 'bg-sky-50 text-sky-700 ring-sky-200',
  HIGH: 'bg-amber-50 text-amber-700 ring-amber-200',
  EMERGENCY: 'bg-rose-50 text-rose-700 ring-rose-200',
  // MaintenanceRequest.status
  DISPATCHED_M20: 'bg-sky-50 text-sky-700 ring-sky-200',
  IN_PROGRESS: 'bg-sky-50 text-sky-700 ring-sky-200',
  RESOLVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // ── M20 Infrastructure — WorksPermit.state ───────────────────────────
  REINSTATEMENT_REVIEW: 'bg-sky-50 text-sky-700 ring-sky-200',
  REWORK_REQUIRED: 'bg-rose-50 text-rose-700 ring-rose-200',
  // BondStatus
  HELD: 'bg-violet-50 text-violet-700 ring-violet-200',
  RELEASED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  FORFEITED: 'bg-rose-50 text-rose-700 ring-rose-200',
  // DigOnceRegister.status
  CONFIRMED: 'bg-sky-50 text-sky-700 ring-sky-200',
  // DefectReport.state
  REPORTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  TRIAGED: 'bg-sky-50 text-sky-700 ring-sky-200',
  SCHEDULED: 'bg-violet-50 text-violet-700 ring-violet-200',
  FIXED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // ContractorRegistry.status
  BLACKLISTED: 'bg-rose-50 text-rose-700 ring-rose-200',
  // ProjectTracker.status
  PROCUREMENT: 'bg-sky-50 text-sky-700 ring-sky-200',
  ON_HOLD: 'bg-amber-50 text-amber-700 ring-amber-200',
  // ProjectMilestone.status
  MET: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  MISSED: 'bg-rose-50 text-rose-700 ring-rose-200',
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone}`}>
      {status.replaceAll('_', ' ')}
    </span>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-slate-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
      {label}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{message}</div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="text-sm font-semibold text-slate-600">{title}</div>
      {hint ? <div className="mt-1 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[8vh]" onClick={onClose}>
      <div
        className={`card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} p-5`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

export function TabBar<K extends string>({ tabs, active, onChange }: { tabs: Array<{ key: K; label: string }>; active: K; onChange: (key: K) => void }) {
  return (
    <div className="mb-4 flex flex-wrap gap-1 rounded-xl bg-slate-200/60 p-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
            active === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function LoadMoreButton({ nextCursor, loading, onClick }: { nextCursor: string | null | undefined; loading: boolean; onClick: () => void }) {
  if (!nextCursor) return null;
  return (
    <div className="mt-4 flex justify-center">
      <button className="btn-secondary" onClick={onClick} disabled={loading}>
        {loading ? 'Loading…' : 'Load more'}
      </button>
    </div>
  );
}

export function BackLink({ to, label = 'Back to list' }: { to: string; label?: string }) {
  return (
    <Link to={to} className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-600 hover:underline">
      ← {label}
    </Link>
  );
}
